/**
 * OpusUploader - one labelled slot per opus piece (see utils/opusImages). The
 * variant keeps the filled slots as one plank list, in piece order.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert } from 'lucide-react';
import { PlankUploader } from './PlankUploader';
import { OPUS_PIECES, OPUS_PIECE_MODULES, isOpusImageCountValid } from '../../../utils/opusImages';

type Slots = (File | null)[];

interface OpusUploaderProps {
  plankGroupId: string;
  files: File[];
  onChange: (files: File[]) => void;
  /** The smallest square's side in cm (the variant's size), to label each piece */
  moduleCm?: number;
}

const toSlots = (files: File[]): Slots => OPUS_PIECES.map((_, i) => files[i] ?? null);

export const OpusUploader: React.FC<OpusUploaderProps> = ({ plankGroupId, files, onChange, moduleCm }) => {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<Slots>(() => toSlots(files));
  // The list last sent up: when the parent hands back anything else, lay it out afresh.
  const emittedRef = useRef<File[]>(files);

  useEffect(() => {
    if (files === emittedRef.current) return;
    emittedRef.current = files;
    setSlots(toSlots(files));
  }, [files]);

  const setSlot = (index: number, file: File | null) => {
    const next = slots.map((f, i) => (i === index ? file : f));
    setSlots(next);
    const joined = next.filter((f): f is File => !!f);
    emittedRef.current = joined;
    onChange(joined);
  };

  const filled = slots.filter(Boolean).length;
  const sizeOf = (i: number) => {
    if (!moduleCm || moduleCm <= 0) return '';
    const [w, h] = OPUS_PIECE_MODULES[i];
    return ` · ${+(w * moduleCm).toFixed(1)}×${+(h * moduleCm).toFixed(1)} cm`;
  };

  return (
    <div className="flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-2 gap-3">
        {OPUS_PIECES.map((piece, i) => (
          <div key={piece} className="flex min-w-0 flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t(`PAGES.NEW_PRODUCT.V3.SURFACE.OPUS_${piece}`)}{sizeOf(i)}
            </p>
            <PlankUploader
              plankGroupId={`${plankGroupId}-opus-${i}`}
              files={slots[i] ? [slots[i] as File] : []}
              onChange={(next) => setSlot(i, next[0] ?? null)}
              maxFiles={1}
              mode="single"
              title={t('PAGES.NEW_PRODUCT.V3.SURFACE.OPUS_UPLOAD_PIECE')}
            />
          </div>
        ))}
      </div>
      {filled > 0 && !isOpusImageCountValid(filled) ? (
        <div className="flex items-center gap-1.5 border border-warning/20 bg-warning/10 px-3 py-1.5 text-xs text-warning">
          <CircleAlert className="size-4 shrink-0" />
          {t('PAGES.NEW_PRODUCT.V3.SURFACE.OPUS_INCOMPLETE', { count: filled })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.SURFACE.OPUS_SIZE_HINT')}</p>
      )}
    </div>
  );
};
